import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Calendar, Shield } from 'lucide-react';

const Profile = () => {
  const { user } = useAuth();
  const [autoScheduleOnUpload, setAutoScheduleOnUpload] = useState(true);
  const [loadingSetting, setLoadingSetting] = useState(false);

  const loadSetting = async () => {
    try {
      const res = await axios.get('/api/auth/settings/auto-schedule');
      setAutoScheduleOnUpload(Boolean(res?.data?.auto_schedule_on_upload));
    } catch {
      // keep default
    }
  };

  useEffect(() => { loadSetting(); }, []);

  const updateSetting = async (nextValue) => {
    try {
      setLoadingSetting(true);
      const res = await axios.put('/api/auth/settings/auto-schedule', {
        auto_schedule_on_upload: nextValue,
      });
      setAutoScheduleOnUpload(Boolean(res?.data?.auto_schedule_on_upload));
      toast.success(`Auto-schedule on upload ${nextValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update setting');
    } finally {
      setLoadingSetting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-gray-600 mt-2">
          Manage your account information and preferences.
        </p>
      </div>

      {/* Profile Card */}
      <div className="card mb-8">
        <div className="flex items-center space-x-6">
          <div className="p-4 bg-purple-100 rounded-full">
            <User className="h-12 w-12 text-purple-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {user?.full_name || 'User'}
            </h2>
            <p className="text-gray-600">{user?.email}</p>
            <p className="text-sm text-gray-500 mt-1">
              Member since {new Date(user?.created_at || Date.now()).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="card mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Account Information</h3>
        
        <div className="space-y-6">
          <div className="flex items-center space-x-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <p className="text-gray-900">{user?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <User className="h-5 w-5 text-gray-400" />
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <p className="text-gray-900">{user?.full_name || 'Not provided'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div>
              <label className="block text-sm font-medium text-gray-700">Member Since</label>
              <p className="text-gray-900">
                {new Date(user?.created_at || Date.now()).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Shield className="h-5 w-5 text-gray-400" />
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Status</label>
              <p className="text-gray-900">Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Automation Settings */}
      <div className="card mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automation Settings</h3>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-medium text-gray-900">Auto-schedule tasks on upload</p>
            <p className="text-sm text-gray-600">Automatically detect tasks in uploaded documents and create schedules.</p>
          </div>
          <button
            disabled={loadingSetting}
            onClick={() => updateSetting(!autoScheduleOnUpload)}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${autoScheduleOnUpload ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}
          >
            {autoScheduleOnUpload ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Account Actions</h3>
        
        <div className="space-y-4">
          <button className="btn-outline w-full sm:w-auto">
            Edit Profile
          </button>
          <button className="btn-outline w-full sm:w-auto ml-0 sm:ml-3">
            Change Password
          </button>
        </div>
        
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-4">Danger Zone</h4>
          <button className="text-red-600 hover:text-red-700 text-sm font-medium">
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
